// A question's ErrorText, when set, replaces whatever message would
// otherwise be shown (the browser's native wording, or a setCustomValidity
// message) for every control that question renders. Looked up via the
// nearest ancestor Form.Group rather than threading the question object
// through every generic handler below.
export const getDisplayedValidationMessage = (target) => {
  const errorText = target
    .closest("[data-error-text]")
    ?.getAttribute("data-error-text");
  return errorText || target.validationMessage;
};

// Renders the validation message inline, as red text under a question's
// control(s), instead of a hover tooltip. One feedback element per question
// (appended to the nearest ancestor Form.Group, which is exactly the group
// getDisplayedValidationMessage's ErrorText lookup already treats as the
// question boundary), so a multi-control question (date parts, a checkbox
// group, Medicare number + IRN) shows a single message rather than one per
// raw control. Message priority is DOM order: the first invalid control
// within the group wins.
export const updateGroupFeedback = (target) => {
  const group = target.closest("[data-error-text]");
  if (!group) return;

  const controls = group.querySelectorAll("input, select, textarea");
  let message = "";
  for (const control of controls) {
    if (typeof control.checkValidity !== "function") continue;
    if (control.className.split(/\s+/).includes("is-invalid")) {
      message = getDisplayedValidationMessage(control);
      if (message) break;
    }
  }

  let feedback = group.querySelector(":scope > .field-invalid-feedback");
  if (!message) {
    if (feedback) feedback.textContent = "";
    return;
  }

  if (!feedback) {
    feedback = document.createElement("div");
    feedback.className = "field-invalid-feedback text-danger small mt-1";
    group.appendChild(feedback);
  }
  feedback.textContent = message;
};

// Single source of truth for Bootstrap is-valid/is-invalid styling, driven
// entirely by the native constraint validation API (required, pattern,
// min/max, minLength/maxLength, and any setCustomValidity a control's own
// onChange handler has applied). onSubmit relaxes the "leave blank fields
// alone" rule so an untouched required control still gets flagged red.
export const applyValidityClass = (target, { onSubmit = false } = {}) => {
  if (typeof target.checkValidity !== "function") return;

  const newClass = target.className
    .replace("is-invalid", "")
    .replace("is-valid", "")
    .trimEnd();

  const isCheckable = target.type === "checkbox" || target.type === "radio";
  const isEmpty = isCheckable ? !target.checked : !target.value;
  const isValid = target.checkValidity();

  if (isEmpty && !onSubmit) {
    target.className = newClass;
    updateGroupFeedback(target);
    return;
  }

  if (isEmpty || isCheckable) {
    target.className = isValid ? newClass : `${newClass} is-invalid`;
    updateGroupFeedback(target);
    return;
  }

  target.className = isValid
    ? `${newClass} is-valid`
    : `${newClass} is-invalid`;
  updateGroupFeedback(target);
};

// Chromium (and other browsers) only evaluate the minlength/maxlength
// constraints (tooShort/tooLong) against input the user actually typed -
// a value set programmatically, even via the native setter workaround, is
// exempt. So a restored value that violates min/maxLength would otherwise
// silently read as valid. This fills that gap without touching whatever a
// field's own onChange already determined (Medicare/etc. keep their own
// more specific message).
export const reconcileLengthConstraint = (el) => {
  if (!el.checkValidity()) return;
  if (
    el.minLength > 0 &&
    el.value.length > 0 &&
    el.value.length < el.minLength
  ) {
    el.setCustomValidity(
      `Please lengthen this text to ${el.minLength} characters or more (you are currently using ${el.value.length} character${el.value.length === 1 ? "" : "s"}).`,
    );
  } else if (el.maxLength > 0 && el.value.length > el.maxLength) {
    el.setCustomValidity(
      `Please shorten this text to ${el.maxLength} characters or less (you are currently using ${el.value.length} characters).`,
    );
  }
};
